import { createFileRoute, Outlet, redirect, isRedirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { refreshManager } from "@/lib/auth";
import { loginRedirectSearch } from "@/lib/login-redirect";
import { useCustomerStore } from "@/store";
import { useAuthGuard } from "@/hooks/use-auth-guard";
import { useMyCustomerContext, resolveActiveMembership } from "@/hooks/use-customer-context";
import { useApplyBrand } from "@/hooks/use-apply-brand";
import { useRememberActivePortal } from "@/hooks/use-remember-active-portal";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { readRememberedPortal } from "@/lib/portal-memory";
import { useActiveDestination } from "@/lib/destinations";
import { AccessDenied } from "@/components/access-denied";
import { AppFrame } from "@/components/shell/app-frame";
import { AppHeader } from "@/components/shell/app-header";
import { BottomBar, Button, PageSkeleton } from "@/components/ui";

export const Route = createFileRoute("/_customer")({
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: loginRedirectSearch(location),
      });
    }
    // Proactive refresh: ensure a valid access token exists before rendering.
    const state = useCustomerStore.getState();
    if (!state.expiresAt || Date.now() >= state.expiresAt - 10_000) {
      try {
        const success = await refreshManager.refresh();
        if (!success) {
          throw redirect({
            to: "/login",
            search: loginRedirectSearch(location),
          });
        }
      } catch (e) {
        if (isRedirect(e)) throw e;
        throw redirect({
          to: "/login",
          search: loginRedirectSearch(location),
        });
      }
    }
  },
  component: CustomerLayout,
});

function CustomerLayout() {
  useAuthGuard();
  const contextQuery = useMyCustomerContext();
  const activeAccountId = useCustomerStore((s) => s.activeAccountId);
  const setActiveAccount = useCustomerStore((s) => s.setActiveAccount);

  const memberships = contextQuery.data?.memberships ?? [];
  const active = resolveActiveMembership(memberships, activeAccountId);

  // Self-heal an absent or stale persisted selection (membership ended) by
  // pinning the store to the resolved membership. The workspaces read the
  // RESOLVED id, not this one, so the heal never causes a second fetch.
  useEffect(() => {
    if (active && active.account_id !== activeAccountId) {
      setActiveAccount(active.account_id);
    }
  }, [active, activeAccountId, setActiveAccount]);

  // The post-auth brand writer for every page this layout draws; `NotFound`
  // writes it where no layout renders. While /me is pending, failed or denied
  // there is no active membership, so the remembered utility's brand holds
  // what the pre-paint drew; with no memory either, nothing is written.
  const remembered = readRememberedPortal()?.branding;
  useApplyBrand(active?.brand_color ?? remembered?.brand_color);
  useRememberActivePortal(active);
  useDocumentTitle(active?.org_name ?? null);
  // One read of the page's destination, so the header's nav and the bottom bar
  // always mark the same one.
  const destination = useActiveDestination();
  const bottomBar = <BottomBar active={destination} />;

  // The frame the workspace will draw in, less the header, which needs the
  // context to name the utility and the person.
  if (contextQuery.isLoading) {
    return (
      <AppFrame header={null} bottomBar={bottomBar}>
        <PageSkeleton />
      </AppFrame>
    );
  }

  // 403 not-an-account-member OR an empty membership list both mean: no portal.
  const denied =
    (contextQuery.isError && (contextQuery.error as { status?: number }).status === 403) ||
    (contextQuery.isSuccess && memberships.length === 0);
  // Any other failure with nothing read (a 5xx, or the network after the
  // read's retries) says the account could not be loaded, never that the
  // person has none.
  if (contextQuery.isError && !denied && !contextQuery.data) {
    return (
      <AppFrame header={null} bottomBar={bottomBar}>
        <AccountLoadError
          orgName={remembered?.org_name ?? null}
          onRetry={() => void contextQuery.refetch()}
        />
      </AppFrame>
    );
  }

  const profile = contextQuery.data?.profile;
  if (denied || !active || !profile) {
    return <AccessDenied />;
  }

  return (
    <AppFrame
      header={
        <AppHeader
          memberships={memberships}
          active={active}
          profile={profile}
          destination={destination}
        />
      }
      bottomBar={bottomBar}
    >
      <Outlet />
    </AppFrame>
  );
}

function AccountLoadError({ orgName, onRetry }: { orgName: string | null; onRetry: () => void }) {
  const { t } = useTranslation("customer");
  return (
    <section className="max-w-[420px] space-y-3 rounded-card bg-surface p-5 shadow-card lg:p-7">
      <h1 className="text-xl font-semibold tracking-[-0.01em] text-ink">
        {orgName ? t("shell.loadError.titleOrg", { org: orgName }) : t("shell.loadError.title")}
      </h1>
      <p className="text-sm text-body">{t("shell.loadError.body")}</p>
      <Button variant="secondary" onClick={onRetry}>
        {t("shell.loadError.retry")}
      </Button>
    </section>
  );
}
